import { Box, Grid } from '@factorialco/deprecated-design-system'
import {
  ContractVersionPageContractVersionFragment,
  ContractVersionsPageContractVersionRequestFragment,
  ContractVersionsPageEmployeeFragment,
  WorkingConditionVersionApplicationFragment,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'

import Actions from './Actions'
import ApprovalBanner from './ApprovalBanner'
import ContractData from './ContractData'
import NewHeader from './NewHeader'
import Tags from './Tags'

interface Props {
  employee: ContractVersionsPageEmployeeFragment
  selectedContractVersion: ContractVersionPageContractVersionFragment
  contractVersionRequest?: ContractVersionsPageContractVersionRequestFragment
  contractVersions: ContractVersionPageContractVersionFragment[]
  workingConditionVersionApplication?: WorkingConditionVersionApplicationFragment
  hasLegacyContractHistoryTimeline?: boolean
  onClickEdit: () => void
  onClickFix: () => void
  onClickDelete: () => void
  toggleHistory: () => void
  setSelectedContractVersion: (contractVersionId: number | undefined) => void
}

const Header = ({
  employee,
  selectedContractVersion,
  contractVersionRequest,
  contractVersions,
  workingConditionVersionApplication,
  onClickEdit,
  onClickFix,
  onClickDelete,
  toggleHistory,
  hasLegacyContractHistoryTimeline,
  setSelectedContractVersion,
}: Props) => {
  const {
    access: { id: accessId },
  } = useCurrentSession()
  const canPromote = selectedContractVersion._policies?.promote?.can ?? false
  const canSeeApprovalRequest = () => {
    if (!canPromote || !contractVersionRequest?.status) return false

    const approversIds = contractVersionRequest?.approversIds || []
    const currentAccessIsApprover = approversIds.includes(accessId)

    return canPromote || currentAccessIsApprover
  }

  const showApprovalBanner = canSeeApprovalRequest() && !!contractVersionRequest

  return (
    <Box gap="s16">
      {showApprovalBanner && (
        <ApprovalBanner
          contractVersionRequest={contractVersionRequest}
          employeeName={employee.fullName ?? ''}
        />
      )}
      {!hasLegacyContractHistoryTimeline ? (
        <NewHeader
          employee={employee}
          selectedContractVersion={selectedContractVersion}
          contractVersions={contractVersions}
          onClickEdit={onClickEdit}
          toggleHistory={toggleHistory}
          hasContractVersionRequest={!!contractVersionRequest}
          onClickFix={onClickFix}
          onClickDelete={onClickDelete}
          setSelectedContractVersion={setSelectedContractVersion}
        />
      ) : (
        <Grid variant="fixed">
          <ContractData
            employeeName={employee.fullName}
            selectedContractVersion={selectedContractVersion}
            contractVersions={contractVersions}
          />
          <Actions
            employeeId={employee.id}
            selectedContractVersion={selectedContractVersion}
            allContractVersions={contractVersions}
            onClickEdit={onClickEdit}
            onClickFix={onClickFix}
            onClickDelete={onClickDelete}
            toggleHistory={toggleHistory}
            hasContractVersionRequest={!!contractVersionRequest}
          />
        </Grid>
      )}
      <Tags
        selectedContractVersion={selectedContractVersion}
        employee={employee}
        workingConditionVersionApplication={workingConditionVersionApplication}
      />
    </Box>
  )
}

export default observer(Header)
